import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Modal } from 'antd';
import { ListResponseSavedReportListItemBean, SavedReportBean } from 'common/api/api.ts';
import { savedReportApi } from 'common/api/entitiesApi.tsx';
import { ListCard } from 'common/components/ListCard';
import { ListActionButton } from 'common/components/ListActionButton.tsx';
import { SearchInput } from 'common/components/SearchInput';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts';
import { apiCall } from 'common/util/Util.tsx';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { reportPeriodLabels } from '../report/Container.tsx';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants.ts'
import { CommonRoutes } from 'common/context/commonRoutes.ts'
import { ReportRequestParametersObjectObject } from 'common/api/api.ts'

export interface SavedReportListProps {
  reportType: ReportRequestParametersObjectObject.TypeEnum
  createRoute?: string
  title?: string
  reportRouteBase?: string
  openRouteSuffix?: string
}

const SavedReportList = ({
  reportType,
  createRoute,
  title = 'Saved Reports',
  reportRouteBase = CommonRoutes.REPORT,
  openRouteSuffix = '/edit?autoLoad=1',
}: SavedReportListProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const appContext = useAppContext()
  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter, clearFilters } = useListFilters(setFilters)
  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<ListResponseSavedReportListItemBean>()
  const [deleteTarget, setDeleteTarget] = useState<SavedReportBean | null>(null)

  useEffect(() => {
    clearFilters()
    setListData(undefined)
  }, [reportType, location.pathname])

  const loadData = () => {
    apiCall(
      setLoading,
      appContext,
      () =>
        savedReportApi.savedReportList(
          String(reportType),
          filters.sort?.name,
          filters.sort?.direction,
          filters.name,
        ),
      (data) => setListData(data),
    )
  }

  const handleDelete = async () => {
    if (!deleteTarget?.id) return
    apiCall(
      setLoading,
      appContext,
      () => savedReportApi.deleteSavedReport(deleteTarget.id as number),
      () => {
        setDeleteTarget(null)
        loadData()
      },
    )
  }

  return (
    <>
      <Modal
        open={!!deleteTarget}
        title="Delete the report"
        okText="Yes"
        cancelText="Cancel"
        onOk={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        destroyOnHidden
      >
        Are you sure you want to delete this saved report?
      </Modal>

      <ListCard
        loading={loading}
        title={title}
        createName="Report"
        createRoute={createRoute || CommonRoutes.REPORT_BUILDER}
        filters={filters}
        setFilters={setFilters}
        listData={listData as any}
        loadData={loadData}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            width: COLUMN_ID_WIDTH,
          },
          {
            title: 'Name',
            dataIndex: 'name',
            className: "list-table-name",
            sorter: true,
            ellipsis: true,
            width: COLUMN_NAME_WIDTH,
            fixed: 'left',
            render: (text: string, record: SavedReportBean) => (
              <a
                style={{ color: '#5888CF' }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigate(`${reportRouteBase}/${record.id}${openRouteSuffix}`)
                }}
              >
                {text}
              </a>
            ),
          },
          {
            title: 'Period',
            dataIndex: 'reportPeriod',
            sorter: true,
            width: 250,
            render: (_: any, record: SavedReportBean) =>
              record.reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE ? (
                <span>{record.startDate + ' - ' + record.endDate}</span>
              ) : (
                <span>{reportPeriodLabels[record.reportPeriod]}</span>
              ),
          },
          {
            title: 'Last Modified',
            dataIndex: 'modified',
            sorter: true,
            width: 200,
            render: (date) => date && dayjs(date).format('DD MMM YYYY'),
          },
          {
            title: 'Actions',
            width: 200,
            render: (_: any, record: SavedReportBean) => (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <ListActionButton
                  onClick={() => navigate(`${reportRouteBase}/${record.id}/copy`)}
                  aria-label="Copy saved report"
                  tooltip='Duplicate'
                >
                  <CopyOutlined />
                </ListActionButton>
                <ListActionButton
                  onClick={() => navigate(`${reportRouteBase}/${record.id}/edit`)}
                  aria-label="Open saved report"
                  tooltip='Edit'
                >
                  <EditOutlined />
                </ListActionButton>
                <ListActionButton danger onClick={() => setDeleteTarget(record)} aria-label="Delete saved report" tooltip='Delete'>
                  <DeleteOutlined />
                </ListActionButton>
              </div>
            ),
          },
        ]}
      >
        <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)} />
      </ListCard>
    </>
  )
}

export default SavedReportList
